import React, { useMemo } from 'react';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import TableRenderers from 'react-pivottable/TableRenderers';
// Namespace import, NOT a default one: Utilities.js is CommonJS with no
// `default` key. Node's ESM interop synthesises one (which is why the server's
// canvasPivot.js can `import PivotUtils from …`), but webpack does not — a
// default import here fails the build with "does not contain a default
// export". The two sides genuinely need different import forms for the same
// file.
import * as PivotUtils from 'react-pivottable/Utilities.js';
import 'react-pivottable/pivottable.css';
import { ui, microLabel } from './canvasUi';

/**
 * The drag-and-drop pivot arranger, shown in the block settings drawer.
 *
 * WHAT IT DOES AND DOESN'T OWN
 *
 * It owns the ARRANGEMENT only. `onChange` hands the caller a clean
 * `{ rows, cols, vals, aggregatorName, rendererName, rowOrder, colOrder }`
 * — the same fields the server's sanitizePivotConfig accepts, and nothing
 * else. react-pivottable's own state object also carries the full dataset and
 * a pile of UI bookkeeping; persisting that into config would balloon the
 * canvas document on every drag.
 *
 * The preview it draws is react-pivottable's own, computed in the browser from
 * `data`. That is fine HERE — it is a live preview of an arrangement being
 * chosen. The card, the email and every export render the server's stored
 * `matrix` instead, so the numbers a reader sees always come from one engine.
 * Both are the same engine anyway (`PivotData`), which is the point of the
 * library choice.
 *
 * Deliberately NOT importing PlotlyRenderers: it would pull Plotly (~3MB) into
 * a CRA build with no code splitting, for chart renderers this block does not
 * offer.
 */

// Only the table renderers. The heatmap variants are safe to expose because
// they change cell styling and never the figures, so the server's `matrix`
// stays a faithful record of what was shown.
const RENDERERS = {
    Table: TableRenderers.Table,
    'Table Heatmap': TableRenderers['Table Heatmap'],
    'Table Col Heatmap': TableRenderers['Table Col Heatmap'],
    'Table Row Heatmap': TableRenderers['Table Row Heatmap'],
};

const s = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { ...microLabel, marginBottom: '2px', display: 'block' },
    hint: { fontSize: '12px', color: ui.ink2, lineHeight: 1.5 },
    // react-pivottable lays out as a wide table; the drawer is ~480px, so the
    // whole widget scrolls horizontally rather than overflowing the panel.
    frame: {
        border: ui.border, borderRadius: ui.radiusSm, padding: '8px',
        overflow: 'auto', maxHeight: '420px', background: ui.surface,
    },
    // On a card: no border (the card has one) and fill the available height
    // rather than capping it, so resizing the block actually gives the pivot
    // more room.
    wrapCard: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
    frameCard: { flex: 1, minHeight: 0, overflow: 'auto', background: ui.surface },
    empty: {
        fontSize: '12px', lineHeight: 1.6, color: ui.ink2, background: '#fafaf9',
        border: `1px dashed ${ui.hairline}`, borderRadius: ui.radiusSm, padding: '11px 13px',
    },
};

/**
 * react-pivottable's state → only the fields we persist.
 *
 * PivotTableUI is FULLY CONTROLLED: it renders from props and calls onChange
 * with the next state, so any field dropped here is a field whose UI reverts
 * on the very next click. That is why `valueFilter` is in this list even
 * though the server does not act on it yet — the per-attribute value
 * checkboxes are always rendered and cannot be hidden, so discarding it made
 * unchecking a value visibly undo itself.
 *
 * react-pivottable applies `valueFilter` itself when building the preview, and
 * `pivotMatrix` applies the same exclusion rule server-side, so the preview
 * and the stored matrix agree.
 */
function toConfig(state) {
    return {
        rows: Array.isArray(state.rows) ? state.rows : [],
        cols: Array.isArray(state.cols) ? state.cols : [],
        vals: Array.isArray(state.vals) ? state.vals : [],
        aggregatorName: state.aggregatorName || 'Sum',
        rendererName: state.rendererName || 'Table',
        rowOrder: state.rowOrder || 'key_a_to_z',
        colOrder: state.colOrder || 'key_a_to_z',
        valueFilter: state.valueFilter && typeof state.valueFilter === 'object' ? state.valueFilter : {},
    };
}

/**
 * `onCard` drops the drawer chrome (heading + hint) and lets the widget fill
 * the block: on a dashboard tile the surrounding card already says what this
 * is, and a fixed max-height would waste the height the user chose by
 * resizing the block.
 */
export default function PivotArranger({ pivot, rows, onChange, onCard = false }) {
    const data = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

    if (data.length === 0) {
        return (
            <div style={s.wrap}>
                {!onCard && <label style={s.label}>Arrangement</label>}
                <div style={s.empty}>
                    Pick a data source and save to run this block — the fields you can pivot by
                    come from the rows it returns.
                </div>
            </div>
        );
    }

    const state = {
        data,
        ...pivot,
        renderers: RENDERERS,
        aggregators: PivotUtils.aggregators,
        // Deliberately NOT sampled. The preview runs over the same rows the
        // server pivoted, so the figures here are the figures that will be
        // stored — a sampled preview would quietly show different totals from
        // the card the user is about to save. Aggregating 2,000 rows is cheap;
        // what costs is laying out distinct keys, which the arrangement itself
        // bounds.
    };

    return (
        <div style={onCard ? s.wrapCard : s.wrap}>
            {!onCard && (
                <>
                    <label style={s.label}>Arrangement</label>
                    <div style={s.hint}>Drag fields into Rows and Columns, pick a measure, then Save.</div>
                </>
            )}
            <div
                style={onCard ? s.frameCard : s.frame}
                data-testid="canvas-pivot-arranger"
                // The card header is the grid's drag handle, but the widget's
                // own drag-and-drop lives in the body. Without stopping
                // propagation here react-grid-layout also sees the pointer
                // and starts moving the BLOCK while the user is dragging a
                // field — the block skates across the dashboard mid-drop.
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
            >
                <PivotTableUI
                    {...state}
                    onChange={(next) => onChange(toConfig(next))}
                    unusedOrientationCutoff={Infinity}
                />
            </div>
        </div>
    );
}
